import { InteractionTag, InteractionTagPrimary } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import React from 'react';

export interface DismissibleTagProps {
  /** Text content of the tag */
  label: string;
  /** Removes the tag. Omit it (or set `disabled`) to render a read-only tag. */
  onDismiss?: () => void;
  /** Renders the tag read-only: no dismiss icon, muted appearance */
  disabled?: boolean;
  /** Accessible name of the dismiss action, e.g. "Klasse 5a entfernen" */
  dismissAriaLabel?: string;
  /** Additional class names for the tag root */
  className?: string;
}

/**
 * Dismissible tag for selected items such as recipients or groups.
 *
 * Wraps Fluent's `InteractionTag`, whose default background equals the page background —
 * which made selected items read as plain text with a stray dismiss icon. The grey tones
 * of the `Tag` component are applied instead (see `styles/_tags.scss`), so a selection in
 * a form and its read-only counterpart in a list view look alike.
 */
export const DismissibleTag: React.FC<DismissibleTagProps> = ({
  label,
  onDismiss,
  disabled,
  dismissAriaLabel,
  className,
}) => {
  const dismissible = !disabled && !!onDismiss;

  return (
    <InteractionTag
      size="small"
      disabled={disabled}
      className={cn('aix-tag-grey', { 'is-disabled': disabled }, className)}
      onClick={dismissible ? onDismiss : undefined}
    >
      <InteractionTagPrimary
        icon={dismissible ? <DismissRegular /> : undefined}
        hasSecondaryAction={false}
        aria-label={dismissible ? dismissAriaLabel : undefined}
      >
        {label}
      </InteractionTagPrimary>
    </InteractionTag>
  );
};
